/**
 * ES About Us — trust & credibility page.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import EsSEO from '@/components/seo/EsSEO';
import useSpanishTrackingNumber from '@/hooks/useSpanishTrackingNumber';

const SITE = 'https://fortworthdental.com';

const SpanishAboutPage = () => {
  const { displayNumber, telLink } = useSpanishTrackingNumber();
  return (
  <>
    <EsSEO
      title="Sobre Nosotros – Fort Worth Dental"
      description="Conoce a nuestro equipo dental bilingüe en Fort Worth. Más de 35 años cuidando sonrisas en Tarrant County. Hablamos español."
      canonical="/es/sobre-nosotros"
      hreflangEn="/about-us"
      schema={{
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        inLanguage: 'es-US',
        url: `${SITE}/es/sobre-nosotros`,
        about: { '@type': 'Dentist', name: 'Fort Worth Dental' },
      }}
    />

    <section className="bg-gradient-to-br from-teal-700 via-teal-600 to-emerald-600 text-white py-16">
      <div className="container mx-auto px-4 max-w-5xl">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Sobre Fort Worth Dental</h1>
        <p className="text-lg text-teal-50 max-w-3xl">
          Más de 35 años cuidando las sonrisas de familias hispanas en Fort Worth y todo el condado de Tarrant.
        </p>
      </div>
    </section>

    <section className="py-16 bg-white">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="prose prose-lg max-w-none">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Nuestra Historia</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Desde 1989, Fort Worth Dental ha sido el hogar dental de confianza para miles de familias en Tarrant County. Lo que comenzó como una práctica familiar pequeña se ha convertido en uno de los consultorios dentales más galardonados del área — reconocidos como <strong>Best of Fort Worth 2025</strong> con más de 1,100 reseñas de 5 estrellas en Google.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Lo que no ha cambiado es nuestro enfoque: tratar a cada paciente como familia, explicar cada procedimiento en el idioma que mejor entiendas (inglés o español), y ofrecer la más alta calidad de atención dental al precio más accesible posible.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-10">Nuestro Equipo</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            <strong>Dr. Mund</strong> — Diplomado del American Board of Oral Implantology con más de 25 años de experiencia en implantología avanzada. Ha colocado más de 10,000 implantes dentales exitosos.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            <strong>Dr. Razo</strong> — Especialista en odontología familiar y cosmética, certificado en técnicas avanzadas de carillas y blanqueamiento profesional. Habla español con fluidez.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            <strong>Dr. Hiwaidi y Dr. Pham</strong> — Complementan nuestro equipo con especialidades en endodoncia, cirugía oral y ortodoncia (Invisalign).
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-10">Nuestro Compromiso Bilingüe</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Entendemos que la atención médica de calidad empieza con la comunicación. Por eso todo nuestro personal está capacitado para atenderte en español — desde la primera llamada hasta el seguimiento después de tu tratamiento.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Explicamos cada procedimiento, opción de seguro y plan de pago en términos claros. Nunca te sentirás presionado ni confundido sobre lo que estás pagando.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-10">Reconocimientos</h2>
          <ul className="text-gray-700 space-y-2 mb-4">
            <li>🏆 <strong>Best of Fort Worth 2025</strong> — Fort Worth Star-Telegram</li>
            <li>⭐ <strong>1,100+ reseñas de 5 estrellas</strong> en Google</li>
            <li>🩺 <strong>American Board of Oral Implantology</strong> — Dr. Mund, Diplomado</li>
            <li>📍 <strong>Atendemos 53 ciudades</strong> en el condado de Tarrant y alrededores</li>
          </ul>
        </div>

        {/* Why Choose Fort Worth Dental — 3 trust cards */}
        <div className="mt-16" data-testid="es-about-why-choose">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">¿Por Qué Elegir Fort Worth Dental?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow text-center">
              <div className="text-5xl mb-4" aria-hidden="true">🦷</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Más de 35 Años de Experiencia</h3>
              <p className="text-gray-600">Atendiendo a Fort Worth desde 1990 — generaciones de familias confían en nuestro equipo.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow text-center">
              <div className="text-5xl mb-4" aria-hidden="true">🏥</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Atención Integral</h3>
              <p className="text-gray-600">Desde limpiezas preventivas hasta implantes complejos y odontología cosmética — todo bajo un mismo techo.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow text-center">
              <div className="text-5xl mb-4" aria-hidden="true">💙</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Enfocados en el Paciente</h3>
              <p className="text-gray-600">Personal bilingüe, planes de pago flexibles y explicaciones claras — tu comodidad es nuestra prioridad.</p>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap justify-center gap-4">
          <Link
            to="/es/contacto"
            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-3 rounded-md"
          >
            Reservar Cita
          </Link>
          <a
            href={telLink}
            className="bg-white border-2 border-teal-600 text-teal-600 font-semibold px-8 py-3 rounded-md hover:bg-teal-50"
          >
            Llamar: {displayNumber}
          </a>
        </div>

        {/* Final CTA banner */}
        <div className="mt-16 bg-teal-600 text-white p-12 rounded-lg text-center" data-testid="es-about-cta">
          <h2 className="text-3xl font-bold mb-4">¿Listo para Vivir la Diferencia de Fort Worth Dental?</h2>
          <p className="text-xl mb-8">Agenda tu primera cita hoy y descubre por qué somos el dentista de confianza de Fort Worth.</p>
          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-6">
            <a href={telLink} className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100">
              Llamar a {displayNumber}
            </a>
            <Link to="/es/contacto" className="bg-yellow-500 text-yellow-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-400">
              Reservar Cita Gratis
            </Link>
          </div>
        </div>
      </div>
    </section>
  </>
  );
};

export default SpanishAboutPage;
